import React, { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Navbar from '../components/navigation/Navbar';
import Card, { CardContent } from '../components/ui/Card';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import MessageBubble, { Message } from '../components/chat/MessageBubble';
import TypingDots from '../components/chat/TypingDots';
import DocumentViewerPanel from '../components/chat/DocumentViewerPanel';
import { Send, Sparkles, ArrowLeft, Bot, AlertCircle } from 'lucide-react';
import { getCurrentBranding } from '../config/branding';
import { UploadedFile } from '../components/documents/FileUpload';
import { streamingService, StreamEvent } from '../services/streamingService';
import { apiService } from '../services/api';
import { getCurrentSession } from '../services/apiAuth';
import SessionService from '../services/sessionService';

const Chat: React.FC = () => {
  const navigate = useNavigate();
  const branding = getCurrentBranding();
  const [currentUser, setCurrentUser] = useState<{ name: string; email: string } | null>(null);
  const [messages, setMessages] = useState<Message[]>([{
    id: 'welcome',
    role: 'assistant',
    content: `Welcome to ${branding.shortName} AI Chat! 🚀\n\nI'm here to help you analyze your trust documents. I'll automatically search across all your uploaded documents to find the most relevant information. Just ask me anything about your documents!`,
    timestamp: Date.now(),
  }]);
  const [input, setInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [, setDocuments] = useState<UploadedFile[]>([]);
  const [currentStreamingMessage, setCurrentStreamingMessage] = useState<Message | null>(null);
  const [statusMessage, setStatusMessage] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [sessionId, setSessionId] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  
  
  // Document viewer panel state
  const [documentViewerOpen, setDocumentViewerOpen] = useState(false);
  const [selectedDocumentId, setSelectedDocumentId] = useState<string>('');
  const [selectedDocumentName, setSelectedDocumentName] = useState<string>('');
  const [highlightedChunks, setHighlightedChunks] = useState<any[]>([]);

  // Load current user session and initialize chat session
  useEffect(() => {
    const session = getCurrentSession();
    if (session?.user) {
      setCurrentUser({
        name: session.user.name,
        email: session.user.email
      });
      // Initialize chat session
      SessionService.getSessionId().then(sessionId => {
        setSessionId(sessionId);
        console.log('Chat session initialized:', sessionId);
      }).catch(error => {
        console.error('Failed to initialize chat session:', error);
        setError('Failed to initialize chat session. Please refresh the page.');
      });
    }
  }, []);

  // Load documents for display purposes (optional)
  useEffect(() => {
    loadDocuments();
  }, []);

  const loadDocuments = async () => {
    try {
      const response = await apiService.getUploadedDocuments();
      if (response.success && response.data) {
        // Convert backend document format to frontend format
        const docs: UploadedFile[] = response.data.map((doc: any) => ({
          id: doc.id.toString(),
          file: new File([''], doc.original_name || doc.filename),
          name: doc.original_name || doc.filename,
          size: parseInt(doc.file_size) || 0,
          type: doc.file_type || 'application/pdf',
          status: 'success' as const,
          progress: 100
        }));
        setDocuments(docs);
      }
    } catch (error) {
      console.error('Failed to load documents:', error);
      // Don't show error for document loading since it's not critical for cross-document search
    }
  };

  // Auto-scroll on new message
  useEffect(() => {
    containerRef.current?.scrollTo({ top: containerRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, isStreaming, currentStreamingMessage]);


  const handleSend = async () => {
    const trimmed = input.trim();
    if (!trimmed || isStreaming) return;

    // Check if session ID is available
    if (!sessionId) {
      setError('Session not initialized. Please refresh the page.');
      return;
    }

    // Clear any previous errors
    setError('');

    const userMsg: Message = { 
      id: Math.random().toString(36).slice(2), 
      role: 'user', 
      content: trimmed, 
      timestamp: Date.now() 
    };
    setMessages(prev => [...prev, userMsg]);
    setInput('');

    // Start streaming response
    setIsStreaming(true);
    setStatusMessage('');
    
    // Create initial streaming message
    const assistantId = Math.random().toString(36).slice(2);
    const initialMessage: Message = {
      id: assistantId,
      role: 'assistant',
      content: '',
      timestamp: Date.now(),
      isStreaming: true
    };
    setCurrentStreamingMessage(initialMessage);

    try {
      let streamingContent = '';
      let sources: any[] = [];
      let confidence: number | undefined;
      let processingTime: number | undefined;
      let tokenCount: number | undefined;

      await streamingService.streamQueryWithFetch(
        {
          question: trimmed,
          sessionId: sessionId || undefined,
          // No documentId - will search across all documents automatically
          options: {
            temperature: 0.3,
            maxTokens: 500
          }
        },
        (event: StreamEvent) => {
          switch (event.type) {
            case 'status':
              setStatusMessage(event.message || '');
              break;
              
            case 'chunk':
              if (event.content) {
                streamingContent += event.content;
                setCurrentStreamingMessage(prev => prev ? {
                  ...prev,
                  content: streamingContent,
                  isStreaming: true
                } : null);
              }
              break;
              
            case 'complete':
              // Save final metadata
              sources = event.sources || [];
              confidence = event.confidence;
              processingTime = event.processingTime;
              tokenCount = event.tokenCount;
              
              // Handle interactive filtering response
              if (event.needsFiltering && event.followUpQuestions) {
                streamingContent = event.message || '';
                // TODO: Handle follow-up questions in UI
                console.log('🔍 Interactive filtering questions:', event.followUpQuestions);
              }
              
              // Clear status
              setStatusMessage('');
              break;
              
            case 'error':
              throw new Error(event.message || 'Streaming failed');
          }
        }
      );

      // Finalize the message
      const finalMessage: Message = {
        id: assistantId,
        role: 'assistant',
        content: streamingContent || "I couldn't generate a response. Please try again.",
        timestamp: Date.now(),
        sources,
        confidence,
        processingTime,
        tokenCount,
        isStreaming: false
      };

      // Add to messages and clear streaming state
      setMessages(prev => [...prev, finalMessage]);
      setCurrentStreamingMessage(null);

    } catch (error) {
      console.error('Streaming error:', error);
      
      const errorMessage: Message = {
        id: assistantId,
        role: 'assistant',
        content: `❌ Sorry, I encountered an error: ${error instanceof Error ? error.message : 'Unknown error'}`,
        timestamp: Date.now(),
        isStreaming: false
      };

      setMessages(prev => [...prev, errorMessage]);
      setCurrentStreamingMessage(null);
      setError(error instanceof Error ? error.message : 'Unknown error occurred');
    } finally {
      setIsStreaming(false);
      setStatusMessage('');
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      // Only send if not streaming
      if (!isStreaming) {
        handleSend();
      }
    }
  };


  // Handle citation clicks to open document viewer
  const handleCitationClick = (documentId: string, documentName: string, chunks: any[]) => {
    setSelectedDocumentId(documentId);
    setSelectedDocumentName(documentName);
    setHighlightedChunks(chunks);
    setDocumentViewerOpen(true);
  };


  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 relative">
      <Navbar 
        user={currentUser || undefined}
        onLogout={() => navigate('/login')}
        transparent
      />
      
      {/* Split Screen Layout */}
      <div className={`pt-20 pb-8 px-4 transition-all duration-300 ${documentViewerOpen ? 'pr-[50vw]' : ''}`}>
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center justify-center gap-3 mb-4"
            >
              <Button
                variant="ghost"
                onClick={() => navigate('/documents')}
                leftIcon={<ArrowLeft className="w-4 h-4" />}
                className="absolute left-0"
              >
                Back to Documents
              </Button>
              <Bot className="w-8 h-8 text-blue-600" />
              <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                AI Legal Assistant
              </h1>
            </motion.div>
            <p className="text-gray-600 text-lg">
              Ask questions about your trust documents and get AI-powered insights
            </p>
          </div>

          {/* Error Display */}
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700"
            >
              <AlertCircle className="w-5 h-5" />
              <span>{error}</span>
            </motion.div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Sidebar */}
            <div className="lg:col-span-1 space-y-6">
            </div>

            {/* Chat Area */}
            <div className="lg:col-span-3 min-w-0">
              <Card className="h-[700px] flex flex-col shadow-xl border-0 bg-white/80 backdrop-blur-sm min-w-0 overflow-hidden">
                <CardContent className="flex-1 flex flex-col p-0 min-w-0 overflow-hidden">
                  {/* Messages */}
                  <div 
                    ref={containerRef}
                    className="flex-1 overflow-y-auto overflow-x-hidden p-4 space-y-4 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 chat-container min-w-0"
                    style={{ scrollBehavior: 'smooth' }}
                  >
                    {messages.map((message) => (
                      <MessageBubble 
                        key={message.id} 
                        message={message} 
                        onCitationClick={handleCitationClick}
                      />
                    ))}
                    
                    {/* Current streaming message */}
                    {currentStreamingMessage && (
                      <MessageBubble 
                        message={currentStreamingMessage} 
                        onCitationClick={handleCitationClick}
                      />
                    )}
                    
                    {/* Status and typing indicator */}
                    {isStreaming && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="flex justify-start"
                      >
                        <div className="max-w-[85%] md:max-w-[70%]">
                          {statusMessage && (
                            <div className="text-sm text-gray-500 mb-2 px-4">
                              <Sparkles className="w-3 h-3 inline mr-1" />
                              {statusMessage}
                            </div>
                          )}
                          {!currentStreamingMessage?.content && (
                            <TypingDots />
                          )}
                        </div>
                      </motion.div>
                    )}
                    
                    {/* Invisible div to scroll to bottom */}
                    <div ref={containerRef} />
                  </div>

                  {/* Error Display */}
                  {error && (
                    <div className="border-t border-gray-200 p-3 bg-red-50 flex items-center gap-2 text-red-700 text-sm">
                      <AlertCircle className="w-4 h-4" />
                      {error}
                    </div>
                  )}

                  {/* Chat Input */}
                  <div className="border-t border-gray-200 p-4 bg-white rounded-b-xl">
                    <div className="flex items-end gap-3">
                      <div className="flex-1">
                        <Input
                          placeholder="Ask about your trust documents..."
                          value={input}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInput(e.target.value)}
                          onKeyPress={handleKeyPress}
                          className="border-0 bg-gray-50 focus:bg-white focus:ring-2 focus:ring-blue-500"
                          disabled={isStreaming}
                        />
                      </div>
                      <Button 
                        onClick={handleSend} 
                        disabled={!input.trim() || isStreaming}
                        className="px-6"
                        rightIcon={<Send className="w-4 h-4" />}
                      >
                        {isStreaming ? 'Analyzing...' : 'Send'}
                      </Button>
                    </div>
                    <div className="mt-2 text-xs flex items-center">
                      <Sparkles className="w-3 h-3 mr-1" />
                      <span className="text-gray-500">Searching across all your documents automatically</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
      
      {/* Document Viewer Panel */}
      <AnimatePresence>
        {documentViewerOpen && (
          <DocumentViewerPanel
            isOpen={documentViewerOpen}
            onClose={() => setDocumentViewerOpen(false)}
            documentId={selectedDocumentId}
            documentName={selectedDocumentName}
            highlightedChunks={highlightedChunks}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default Chat;
